import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Search, X } from 'lucide-react';
import { registerDataAreaLaunchModal } from '../utils/platformNavigation';

/** Friendly labels for common AX / HK DataAreaId codes. */
const DATA_AREA_LABELS = {
  dk: 'Denmark',
  da: 'Denmark',
  no: 'Norway',
  se: 'Sweden',
  fi: 'Finland',
  is: 'Iceland',
  de: 'Germany',
  at: 'Austria',
  ch: 'Switzerland',
  nl: 'Netherlands',
  be: 'Belgium',
  fr: 'France',
  lu: 'Luxembourg',
  gb: 'United Kingdom',
  uk: 'United Kingdom',
  ie: 'Ireland',
  es: 'Spain',
  pt: 'Portugal',
  it: 'Italy',
  pl: 'Poland',
  cz: 'Czech Republic',
  sk: 'Slovakia',
  hu: 'Hungary',
  ro: 'Romania',
  bg: 'Bulgaria',
  hr: 'Croatia',
  si: 'Slovenia',
  ee: 'Estonia',
  lv: 'Latvia',
  lt: 'Lithuania',
  us: 'United States',
  ca: 'Canada',
  au: 'Australia',
  nz: 'New Zealand',
  jp: 'Japan',
  cn: 'China',
  hk: 'Hong Kong',
  sg: 'Singapore',
  ae: 'United Arab Emirates',
  sa: 'Saudi Arabia',
  za: 'South Africa',
  br: 'Brazil',
  mx: 'Mexico',
  gl: 'Greenland',
  fo: 'Faroe Islands',
};

function normalizeDataAreaId(value) {
  return String(value || '').trim();
}

function dataAreaLabel(dataAreaId) {
  const raw = normalizeDataAreaId(dataAreaId);
  if (!raw) return 'Unknown';
  const key = raw.toLowerCase();
  if (DATA_AREA_LABELS[key]) return DATA_AREA_LABELS[key];
  // "Amazon Germany" style already human-readable names
  if (/[a-z].*[A-Z]| /.test(raw) || raw.length > 4) return raw;
  return raw.toUpperCase();
}

function dataAreaCode(dataAreaId) {
  const raw = normalizeDataAreaId(dataAreaId);
  if (!raw) return '—';
  if (raw.length <= 6 && !/\s/.test(raw)) return raw.toUpperCase();
  // Human-readable names: use initials (e.g. "Amazon Germany" → "AG")
  const parts = raw.split(/[\s/_-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return parts
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase();
  }
  return raw.slice(0, 2).toUpperCase();
}

function hostFromUrl(url) {
  try {
    const parsed = new URL(String(url || '').trim());
    return parsed.host || '';
  } catch {
    return '';
  }
}

/**
 * Global host for choosing DataAreaId when a platform has multiple company URLs.
 * Mount once under ShellLayout.
 */
export function PlatformDataAreaLaunchModalHost() {
  const [request, setRequest] = useState(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => registerDataAreaLaunchModal(setRequest), []);

  useEffect(() => {
    if (!request) {
      setQuery('');
      setActiveIndex(0);
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => searchRef.current?.focus(), 40);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(focusTimer);
    };
  }, [request]);

  const toolName = String(request?.tool?.name || request?.tool?.key || 'platform');
  const options = useMemo(
    () => (Array.isArray(request?.options) ? request.options : []),
    [request]
  );

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => {
      const id = normalizeDataAreaId(option.dataAreaId).toLowerCase();
      const label = dataAreaLabel(option.dataAreaId).toLowerCase();
      const host = hostFromUrl(option.url).toLowerCase();
      return id.includes(q) || label.includes(q) || host.includes(q);
    });
  }, [options, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, request]);

  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, filteredOptions]);

  const close = () => {
    request?.onCancel?.();
    setRequest(null);
  };

  const pick = (option) => {
    if (!option) return;
    request?.onPick?.(option);
    setRequest(null);
  };

  const onKeyDown = (event) => {
    if (!request) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (!filteredOptions.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % filteredOptions.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + filteredOptions.length) % filteredOptions.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      pick(filteredOptions[activeIndex] || filteredOptions[0]);
    }
  };

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      {request && (
        <motion.div
          className="modal-backdrop platform-data-area-modal-backdrop is-open"
          onClick={close}
          onKeyDown={onKeyDown}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <motion.div
            className="modal-card platform-data-area-modal is-open"
            role="dialog"
            aria-modal="true"
            aria-labelledby="platform-data-area-modal-title"
            onClick={(event) => event.stopPropagation()}
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="platform-data-area-modal-header">
              <div className="platform-data-area-modal-heading">
                <p className="platform-data-area-modal-eyebrow">{toolName}</p>
                <h3 id="platform-data-area-modal-title">Where do you want to go?</h3>
                <p className="hint-text platform-data-area-modal-subtitle">
                  Choose a country or company to open. You can change this next time you launch the platform.
                </p>
              </div>
              <button
                type="button"
                className="admin-platform-editor-close"
                aria-label="Close"
                onClick={close}
              >
                <X size={18} />
              </button>
            </div>

            {options.length > 5 && (
              <label className="platform-data-area-search">
                <Search size={16} aria-hidden="true" />
                <input
                  ref={searchRef}
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search country or code…"
                  autoComplete="off"
                  aria-label="Search countries"
                />
              </label>
            )}

            <div
              className="platform-data-area-modal-list"
              ref={listRef}
              role="listbox"
              aria-label="Available countries"
            >
              {filteredOptions.map((option, index) => {
                const label = dataAreaLabel(option.dataAreaId);
                const code = dataAreaCode(option.dataAreaId);
                const host = hostFromUrl(option.url);
                const isActive = index === activeIndex;
                return (
                  <button
                    key={`${option.dataAreaId}-${option.url}`}
                    type="button"
                    className={isActive ? 'platform-data-area-option is-active' : 'platform-data-area-option'}
                    role="option"
                    aria-selected={isActive}
                    data-active={isActive ? 'true' : 'false'}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => pick(option)}
                  >
                    <span className="platform-data-area-option-badge" aria-hidden="true">
                      {code}
                    </span>
                    <span className="platform-data-area-option-copy">
                      <strong>{label}</strong>
                      <span className="hint-text">
                        {[code !== label.toUpperCase() ? code : null, host || null]
                          .filter(Boolean)
                          .join(' · ') || 'Open platform'}
                      </span>
                    </span>
                    <span className="platform-data-area-option-go" aria-hidden="true">
                      <ArrowRight size={16} />
                    </span>
                  </button>
                );
              })}
              {filteredOptions.length === 0 && (
                <div className="platform-data-area-empty">
                  No matches for “{query.trim()}”. Try another name or code.
                </div>
              )}
            </div>

            <div className="platform-data-area-modal-footer">
              <span className="platform-data-area-modal-hint">
                {filteredOptions.length} of {options.length} available
                {options.length > 1 ? ' · ↑↓ Enter' : ''}
              </span>
              <button type="button" className="ghost-button admin-inline-button" onClick={close}>
                Cancel
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
